import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from "react-image-crop";
import setCanvasPreview from "../utility/setCanvasPreview";
import 'react-image-crop/dist/ReactCrop.css';

const ASPECT_RATIO = 16 / 9;
const MIN_DIMENSION = 150;

const ImageCropper = ({ initialImages, closeModal, updateAvatar }) => {
  const [state, setState] = useState({
    imgSrc: [],
    crops: [],
    error: "",
  });

  const imgRefs = useRef([]);
  const previewCanvasRef = useRef(null);

  const processImage = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const { naturalWidth, naturalHeight } = img;
          if (naturalWidth < MIN_DIMENSION || naturalHeight < MIN_DIMENSION) {
            reject("Image must be at least 150 x 150 pixels.");
          } else {
            resolve({
              src: event.target.result,
              crop: makeAspectCrop(
                { unit: "%", width: (MIN_DIMENSION / naturalWidth) * 100 },
                ASPECT_RATIO,
                naturalWidth,
                naturalHeight
              ),
            });
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    });
  }, []);

  useLayoutEffect(() => {
    if (initialImages && initialImages.length > 0) {
      Promise.all(initialImages.map(processImage))
        .then((results) => {
          setState({
            imgSrc: results.map((r) => r.src),
            crops: results.map((r) => r.crop),
            error: "",
          });
        })
        .catch((error) => {
          setState((prevState) => ({ ...prevState, error }));
        });
    }
  }, [initialImages, processImage]);

  const onImageLoad = useCallback((e, index) => {
    const { width, height } = e.currentTarget;
    const crop = centerCrop(
      makeAspectCrop(
        { unit: "%", width: (MIN_DIMENSION / width) * 100 },
        ASPECT_RATIO,
        width,
        height
      ),
      width,
      height
    );
    setState((prevState) => ({
      ...prevState,
      crops: prevState.crops.map((c, i) => (i === index ? crop : c)),
    }));
  }, []);

  const handleCropChange = useCallback((index, crop) => {
    setState((prevState) => ({
      ...prevState,
      crops: prevState.crops.map((c, i) => (i === index ? crop : c)),
    }));
  }, []);

  const handleCropAllImages = useCallback(() => {
    const croppedImages = state.imgSrc.map((src, index) => {
      return new Promise((resolve) => {
        setCanvasPreview(
          imgRefs.current[index],
          previewCanvasRef.current,
          convertToPixelCrop(
            state.crops[index],
            imgRefs.current[index].width,
            imgRefs.current[index].height
          )
        );

        previewCanvasRef.current.toBlob((blob) => {
          const file = new File([blob], `cropped-image-${index}.png`, {
            type: "image/png",
          });
          resolve(file);
        }, "image/png");
      });
    });

    Promise.all(croppedImages).then((files) => {
      updateAvatar(files);
      closeModal();
    });
  }, [state.imgSrc, state.crops, updateAvatar, closeModal]);

  return (
    <>
      {state.error && <p className="text-red-400 text-xs">{state.error}</p>}
      {state.imgSrc.length > 0 && (
        <div className="flex flex-col items-center">
          {state.imgSrc.map((image, index) => (
            <div key={index} className="mb-4">
              <ReactCrop
                crop={state.crops[index]}
                onChange={(_, percentCrop) => handleCropChange(index, percentCrop)}
                keepSelection
                aspect={ASPECT_RATIO}
                minWidth={MIN_DIMENSION}
              >
                <img
                  ref={(el) => (imgRefs.current[index] = el)}
                  src={image}
                  alt="Upload"
                  style={{ maxHeight: "50vh" }}
                  onLoad={(e) => onImageLoad(e, index)}
                />
              </ReactCrop>
            </div>
          ))}
          <div
            className="text-white font-mono text-xs py-2 px-4 rounded-2xl mt-4 bg-sky-500 hover:bg-sky-600 cursor-pointer"
            onClick={handleCropAllImages}
          >
            Crop Images
          </div>
        </div>
      )}
      <canvas
        ref={previewCanvasRef}
        className="mt-4"
        style={{
          display: "none",
          border: "1px solid black",
          objectFit: "contain",
          width: 150,
          height: 150,
        }}
      />
    </>
  );
};

export default ImageCropper;